import React, { useEffect, useState } from 'react'
import cn from 'classnames'
import { Box, Button, Grid, makeStyles, Typography, Theme } from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'

import { Video } from 'components'
import { createInteraction, EventReplays } from 'helpers'
import { GameFlowStepsConfig } from 'helpers/steps'
import { GameFlowSteps, IReplayItem } from 'types'
import { useAppState } from '../../providers'

export const EventReplay = ({ setScene }) => {
  const {
    appState: { user, eventStage }
  } = useAppState()
  const classes = useStyles({})
  const [replay, setReplay] = useState<IReplayItem>(EventReplays['fullShow'])
  const [renderDemo, setRenderDemo] = useState<boolean>(false)
  const [playTime, setPlayTime] = useState<number>(0)

  const sendPlayTime = () => {
    if (playTime > 0) {
      createInteraction(replay?.id || '', user?.email || '', 'eventReplay', 'watchTime', eventStage, playTime + '')
      setPlayTime(0)
    }
  }

  useEffect(() => {
    createInteraction('fullShow' || '', user?.email || '', 'eventReplay', 'viewed', eventStage)
    setTimeout(() => {
      setRenderDemo(true)
    }, GameFlowStepsConfig[GameFlowSteps.Demo].animation.time)
    // eslint-disable-next-line
  }, [])

  return (
    <div className={classes.root}>
      {renderDemo && (
        <Grid className={classes.mainContainer} container direction='row'>
          <Grid item xs={12} md={9} className={classes.demoContainer}>
            <Video videoSrc={replay.videoSrc} posterSrc={replay.poster} setPlayTime={setPlayTime} />
          </Grid>
          <Grid item xs={12} md={3} className={classes.endContainer} container direction={'column'}>
            <Grid item xs={6} className={classes.endContentContainer} container alignItems='flex-start' direction='row'>
              <Grid item xs={12}>
                <Typography
                  variant='h1'
                  className={cn(classes.header, {
                    [classes.smallHeader]: replay.title?.length > 40
                  })}
                >
                  {replay.title}
                </Typography>

                <Typography component='p' className={classes.body} paragraph>
                  {replay.subtitle}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              xs={6}
              className={cn(classes.endContentContainer, classes.endContentThumbnail)}
              container
              alignItems='flex-end'
              direction='row'
            >
              <Grid xs={12} container direction={'row'} alignItems='flex-end'>
                <Grid item xs={12}>
                  <Typography variant='h1' className={classes.subHeader}>
                    Next video
                  </Typography>
                </Grid>
              </Grid>
              <Grid xs={12}>
                <Box flexGrow={1}>
                  <figure
                    className={classes.thumbnail}
                    onClick={() => {
                      sendPlayTime()
                      createInteraction(replay.next || '', user?.email || '', 'eventReplay', 'viewed', eventStage)
                      setReplay(EventReplays[replay.next])
                    }}
                  >
                    <img src={EventReplays[replay.next].thumbnail} alt='Video thumbnail' />
                    <figcaption>
                      <Typography
                        variant='h2'
                        className={cn(classes.subHeader)}
                        dangerouslySetInnerHTML={{ __html: EventReplays[replay.next].title || '' }}
                      />
                    </figcaption>
                  </figure>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Box display='flex' className={classes.contentActionBox}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                sendPlayTime()
                setScene(GameFlowSteps.Welcome)
              }}
            >
              Back
            </Button>
          </Box>
        </Grid>
      )}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 'calc(100% - 64px)',
    bottom: 60,
    top: 115,
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
    backgroundColor: 'transparent',
    color: '#000',
    justifyContent: 'center',

    [theme.breakpoints.down('sm')]: {
      width: '100%',
      top: 70
    }
  },
  contentActionBox: {
    marginTop: 27,

    '& .MuiButton-root': {
      marginRight: theme.spacing(4),
      marginTop: 0,
      marginBottom: 0
    }
  },
  mainContainer: {
    opacity: 1,
    animationName: '$fadeInOpacity',
    animationIterationCount: 1,
    animationTimingFunction: 'ease-in',
    animationDuration: '0.75s',
    width: '100%',
    maxWidth: 1530,
    padding: '2rem 5rem',

    [theme.breakpoints.down('lg')]: {
      paddingRight: '3rem',
      paddingLeft: '3rem'
    },

    [theme.breakpoints.down('sm')]: {
      height: '100%',
      overflowY: 'auto',
      paddingRight: '5rem',
      paddingLeft: '5rem'
    }
  },
  demoContainer: {
    paddingRight: '5rem',
    [theme.breakpoints.down('lg')]: {
      paddingRight: '3rem'
    },
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
      paddingBottom: '1rem'
    }
  },
  videoContainer: {
    height: '100%',
    width: '100%'
  },
  endContainer: {
    justifyContent: 'center',
    minHeight: '100%'
  },
  endContentContainer: {
    maxWidth: '100%',
    minHeight: '50%',
    '& .MuiGrid-item': {
      padding: '5px 0'
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 'unset'
    }
  },
  endContentThumbnail: {
    borderTop: '1px solid #DBDBDB',
    [theme.breakpoints.only('md')]: {
      marginTop: '20px',
      marginBottom: '-20px'
    }
  },
  header: {
    fontSize: '26px',
    lineHeight: '1.2',
    fontWeight: 700,
    marginBottom: '.5rem',
    [theme.breakpoints.only('md')]: {
      fontSize: '24px !important'
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '20px !important'
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '16px !important'
    }
  },
  smallHeader: {
    fontSize: '24px',
    lineHeight: '1.2',
    fontWeight: 700,
    marginBottom: '.5rem',
    [theme.breakpoints.only('md')]: {
      fontSize: '20px !important'
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px !important'
    }
  },
  body: {
    [theme.breakpoints.only('md')]: {
      fontSize: '16px !important'
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px !important'
    }
  },
  subHeader: {
    fontSize: '20px',
    fontFamily: 'Verizon-Bold'
  },
  thumbnail: {
    cursor: 'pointer',
    position: 'relative',

    '& > img': {
      width: '100%'
    },

    '& > figcaption': {
      bottom: 0,
      color: theme.palette.common.white,
      padding: theme.spacing(2),
      position: 'absolute',
      textShadow: `1px 1px 2px ${theme.palette.common.black}`
    }
  },
  '@keyframes fadeInOpacity': {
    '0%': {
      opacity: 0
    },
    '100%': {
      opacity: 1
    }
  },
  '@keyframes fadeOutOpacity': {
    '0%': {
      opacity: 1
    },
    '100%': {
      opacity: 0
    }
  }
}))
